import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const PACKAGES = [
  { id: 'start',   profit: 29, min: 2700 },
  { id: 'balance', profit: 37, min: 5800 },
  { id: 'premium', profit: 49, min: 9800 },
];

const useCountUp = (target: number, duration = 600) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const animate = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round((target) * ease));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);
  return val;
};

const Calculator = () => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(5800);
  const [selectedPkg, setSelectedPkg] = useState('balance');

  const pkg = PACKAGES.find(p => p.id === selectedPkg) || PACKAGES[1];
  const totalProfit = Math.round(amount * (pkg.profit / 100));
  const totalAmount = amount + totalProfit;

  const displayProfit = useCountUp(totalProfit);
  const displayTotal = useCountUp(totalAmount);

  const pkgNameKeys: Record<string, string> = {
    start: 'pkg.start.name',
    balance: 'pkg.balance.name',
    premium: 'pkg.premium.name',
  };

  return (
    <section id="calculator" className="py-20 md:py-28" style={{ background: 'linear-gradient(180deg, #111418 0%, #0d1117 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-calculator-line text-green-400 text-sm"></i>
            <span className="text-green-400 text-xs font-semibold uppercase tracking-wide">{t('calc.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl xl:text-5xl font-black text-white mb-4">
            {t('calc.title_p1')} <span className="text-green-400">{t('calc.title_hl')}</span>
          </h2>
          <p className="text-white/50 text-base">{t('calc.subtitle')}</p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left: inputs */}
            <div className="p-8 md:p-10">
              <h3 className="text-gray-900 font-bold text-xl mb-7">{t('calc.params')}</h3>

              {/* Amount */}
              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">
                  {t('calc.amount')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg">€</span>
                  <input
                    type="number"
                    value={amount}
                    min={100}
                    max={100000}
                    onChange={e => setAmount(Math.max(100, parseInt(e.target.value) || 100))}
                    className="w-full pl-10 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-bold text-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-sm"
                  />
                </div>
              </div>

              {/* Package selector */}
              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">
                  {t('calc.package')}
                </label>
                <div className="space-y-2">
                  {PACKAGES.map(p => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPkg(p.id); setAmount(p.min); }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all duration-150 cursor-pointer ${
                        selectedPkg === p.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          selectedPkg === p.id ? 'border-green-500' : 'border-gray-300'
                        }`}>
                          {selectedPkg === p.id && <div className="w-2 h-2 bg-green-500 rounded-full"></div>}
                        </div>
                        <span className={`text-sm font-semibold ${selectedPkg === p.id ? 'text-green-700' : 'text-gray-700'}`}>
                          {t(pkgNameKeys[p.id])}
                        </span>
                      </div>
                      <span className={`text-sm font-black ${selectedPkg === p.id ? 'text-green-500' : 'text-gray-400'}`}>
                        {p.profit}%
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fixed period info */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-time-line text-green-500"></i>
                  <span className="text-green-700 text-xs font-semibold uppercase tracking-wider">{t('calc.dur.label')}</span>
                </div>
                <div className="text-green-900 font-black text-2xl">{t('calc.dur.val')}</div>
                <p className="text-green-600/70 text-xs mt-1">{t('calc.dur.fixed')}</p>
              </div>
            </div>

            {/* Right: results */}
            <div className="bg-gradient-to-br from-green-600 to-green-800 p-8 md:p-10 flex flex-col justify-between">
              <div>
                <h3 className="text-white/70 font-semibold text-sm uppercase tracking-wider mb-7">{t('calc.result')}</h3>

                <div className="mb-6">
                  <div className="text-white/60 text-xs uppercase tracking-wider mb-1">{t('calc.expected')}</div>
                  <div className="flex items-end gap-2">
                    <span className="text-white font-black text-5xl">€{displayProfit.toLocaleString()}</span>
                    <div className="mb-2 flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5">
                      <i className="ri-arrow-up-line text-white text-xs"></i>
                      <span className="text-white text-xs font-semibold">{pkg.profit}{t('calc.per2days')}</span>
                    </div>
                  </div>
                </div>

                <div className="w-full h-px bg-white/20 mb-6"></div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc.total')}</span>
                    <span className="text-white font-bold text-xl">€{displayTotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc.invested')}</span>
                    <span className="text-white font-semibold">€{amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc.pkg.label')}</span>
                    <span className="text-white font-semibold">{t(pkgNameKeys[pkg.id])}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc.period')}</span>
                    <span className="text-white font-semibold">{t('calc.period.val')}</span>
                  </div>
                </div>

                {/* Visual bar */}
                <div className="mt-6">
                  <div className="flex justify-between text-xs text-white/50 mb-1.5">
                    <span>{t('calc.bar.invested')}</span>
                    <span>{t('calc.bar.profit')}</span>
                  </div>
                  <div className="flex rounded-full overflow-hidden h-3 bg-white/20">
                    <div className="bg-white transition-all duration-700 rounded-full"
                      style={{ width: `${(amount / totalAmount) * 100}%` }} />
                    <div className="bg-green-300 transition-all duration-700"
                      style={{ width: `${(totalProfit / totalAmount) * 100}%` }} />
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  const el = document.getElementById('packages');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="mt-8 w-full bg-white hover:bg-gray-50 text-green-700 font-bold py-4 rounded-xl transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap text-sm"
              >
                {t('calc.cta')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
